import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import OAuthConsent from "@/pages/OAuthConsent";
import OAuthLiveDebug from "@/pages/OAuthLiveDebug";
import TlmsDebugTerminal from "@/pages/TlmsDebugTerminal";
import Dashboard from "@/pages/Dashboard";
import Partners from "@/pages/Partners";
import Customers from "@/pages/Customers";
import Permissions from "@/pages/Permissions";
import Apps from "@/pages/Apps";
import AppViewer from "@/pages/AppViewer";
import ActivityLogs from "@/pages/ActivityLogs";
import KnowledgeBase from "@/pages/KnowledgeBase";
import KBCategory from "@/pages/KBCategory";
import KBCategoryPrint from "@/pages/KBCategoryPrint";
import KBArticle from "@/pages/KBArticle";
import KBMissingAssets from "@/pages/KBMissingAssets";
import KBManageCategories from "@/pages/KBManageCategories";
import KBDuplicates from "@/pages/KBDuplicates";
import KBSnippets from "@/pages/KBSnippets";
import KBCalloutAudit from "@/pages/KBCalloutAudit";
import KBRiskReview from "@/pages/KBRiskReview";
import KBImageHealth from "@/pages/KBImageHealth";
import KBCleaned from "@/pages/KBCleaned";
import KBDangerReview from "@/pages/KBDangerReview";
import KBPlanner from "@/pages/KBPlanner";
import KBVisibility from "@/pages/KBVisibility";
import KBBulkVisibility from "@/pages/KBBulkVisibility";
import KBCategoryHealth from "@/pages/KBCategoryHealth";
import KBSearchInsights from "@/pages/KBSearchInsights";
import KBSuggestions from "@/pages/KBSuggestions";
import KBUsage from "@/pages/KBUsage";
import KBFeedbackDashboard from "@/pages/KBFeedbackDashboard";
import KBGlossary from "@/pages/KBGlossary";
import Analytics from "@/pages/Analytics";
import Requests from "@/pages/Requests";
import Developer from "@/pages/Developer";
import Quotes from "@/pages/Quotes";
import Inventory from "@/pages/Inventory";
import Hardware from "@/pages/Hardware";
import Manufacturers from "@/pages/Manufacturers";
import SipTrunks from "@/pages/SipTrunks";
import InventoryHub from "@/pages/InventoryHub";
import NetworkAssessmentKeys from "@/pages/NetworkAssessmentKeys";
import NumberOrders from "@/pages/NumberOrders";
import Fax from "@/pages/Fax";
import Commissions from "@/pages/Commissions";
import PartnerCommissions from "@/pages/PartnerCommissions";
import PaymentsTimeline from "@/pages/PaymentsTimeline";
import AccountingQueue from "@/pages/AccountingQueue";
import CommissionsEngine from "@/pages/CommissionsEngine";
import InOut from "@/pages/InOut";
import TeamPage from "@/pages/TeamPage";
import Teams from "@/pages/Teams";
import Projects from "@/pages/Projects";
import QBR from "@/pages/QBR";
import DidMaster from "@/pages/DidMaster";
import ApiKeyMaster from "@/pages/ApiKeyMaster";
import SipTraceAnalysis from "@/pages/SipTraceAnalysis";
import ServiceDesk from "@/pages/ServiceDesk";
import AskClaude from "@/pages/AskClaude";
import Helpdesk from "@/pages/Helpdesk";
import Tasks from "@/pages/Tasks";
import AIOutbound from "@/pages/AIOutbound";
import JobBoard from "@/pages/JobBoard";
import UCHospitality from "@/pages/UCHospitality";
import AppUnavailable from "@/pages/AppUnavailable";
import ErrorPage from "@/pages/ErrorPage";
import Changelog from "@/pages/Changelog";
import PriceList from "@/pages/PriceList";
import BillingSync from "@/pages/BillingSync";
import HumiLayout from "@/pages/humi/HumiLayout";
import HumiHome from "@/pages/humi/HumiHome";
import HumiPeople from "@/pages/humi/HumiPeople";
import HumiTimeOff from "@/pages/humi/HumiTimeOff";
import { makeHumiPlaceholder } from "@/pages/humi/HumiPlaceholder";
import Profile from "@/pages/Profile";
import { FileText, GraduationCap, ClipboardList, TrendingUp,
         BarChart3, ShieldCheck, Building2, Plug } from "lucide-react";
const HumiProfile     = Profile;  // Iter 103 — real profile form
const HumiDocuments   = makeHumiPlaceholder("Documents",   "Contracts, policies, tax forms and other HR documents will live here.", FileText);
const HumiTraining    = makeHumiPlaceholder("Training",    "Assigned courses, completion tracking, and training-vendor deep-links will live here.", GraduationCap);
const HumiSurveys     = makeHumiPlaceholder("Surveys",     "Engagement and pulse surveys — HR publishes, everyone responds.", ClipboardList);
const HumiPerformance = makeHumiPlaceholder("Performance", "Quarterly reviews, goals, 1-on-1 notes and feedback threads.", TrendingUp);
const HumiReporting   = makeHumiPlaceholder("Reporting",   "Headcount, turnover, PTO utilization, and diversity reports (HR only).", BarChart3);
const HumiBenefits    = makeHumiPlaceholder("Benefits",    "Health, dental, RRSP and other benefit programs and enrollment.", ShieldCheck);
const HumiCompany     = makeHumiPlaceholder("Company",     "Org chart, mission, brand assets and company-wide announcements.", Building2);
const HumiAddons      = makeHumiPlaceholder("Add-ons",     "Third-party integrations (training vendor, HRIS, payroll, etc.).", Plug);
import Reports from "@/pages/Reports";
import TerritoryAliases from "@/pages/TerritoryAliases";
import EnvDrift from "@/pages/EnvDrift";
import DataSync from "@/pages/DataSync";
import UCCDR from "@/pages/UCCDR";
import EmergencyNumbers from "@/pages/EmergencyNumbers";
import KBArticleEditor from "@/pages/KBArticleEditor";
import KBManageArticles from "@/pages/KBManageArticles";
import KBContentStudio from "@/pages/KBContentStudio";
import Productivity from "@/pages/Productivity";
import SettingsPage from "@/pages/Settings";
import QuoteAccept from "@/pages/QuoteAccept";

function FullLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
    </div>
  );
}

function Protected({ children, perm, anyPerm, superOnly, staffOnly, allowEmails }) {
  const { user, loading, has, blockedRoutes } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) {
    // Iter 101 — Preserve the intended deep-link across the login round-
    // trip. Grab the current path + search + hash and hand it to
    // /login as `next`. Login.jsx validates the value is a same-origin
    // path (open-redirect defense) and navigates there after both
    // password login and MFA verify. Skip when the current path is
    // already /login or /dashboard to keep URLs clean.
    const here = `${location.pathname}${location.search}${location.hash}`;
    const skipNext = here === "/" || here.startsWith("/login") || here.startsWith("/dashboard");
    const target = skipNext ? "/login?reason=signin" : `/login?reason=signin&next=${encodeURIComponent(here)}`;
    return <Navigate to={target} replace />;
  }
  // Iter 120 — Deny with a generic 403 screen instead of a silent bounce to
  // /dashboard, so a pasted or bookmarked URL explains itself.
  if (superOnly && user.role !== "super_admin" && user.base_role !== "super_admin") {
    return <Layout><ErrorPage code={403} /></Layout>;
  }
  // Internal tools stay closed to partner / customer tenants whatever
  // permissions their role carries (the backend enforces the same).
  if (staffOnly && ["partner", "customer"].includes(user.base_role)) {
    return <Layout><ErrorPage code={403} /></Layout>;
  }
  // Iter 157 — Data Sync is limited to two named operators; the backend
  // enforces the same list.
  if (allowEmails && !allowEmails.includes((user.email || "").trim().toLowerCase())) {
    return <Layout><ErrorPage code={403} /></Layout>;
  }
  // Iter 119e — An app marked Inactive closes its page for everyone, super
  // admins included. The app itself stays visible in Settings → Apps.
  const path = location.pathname;
  const blockedApp = (blockedRoutes || []).find(
    (b) => b.route !== "/dashboard" && b.route !== "/"
      && (path === b.route || path.startsWith(`${b.route}/`)));
  if (blockedApp) {
    return (
      <Layout>
        <AppUnavailable appName={blockedApp.name} inactive={blockedApp.inactive} />
      </Layout>
    );
  }
  if (perm && !has(perm)) return <Layout><ErrorPage code={403} /></Layout>;
  // Iter 106c — Some routes are gated by ANY-of a set of perms
  // (e.g. /activity is reachable with either activity_logs.view OR
  // system_logs.view — each unlocks a different panel).
  if (anyPerm && anyPerm.length && !anyPerm.some((p) => has(p))) {
    return <Layout><ErrorPage code={403} /></Layout>;
  }
  return <Layout>{children}</Layout>;
}

/** Iter 120 — Unknown URL: show a generic 404 (inside the shell when signed
 * in, standalone otherwise) instead of silently redirecting to /dashboard. */
function NotFound() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <ErrorPage code={404} signedIn={false} />;
  return <Layout><ErrorPage code={404} /></Layout>;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* Iter 101 Phase 2 — OIDC consent screen. Backend /api/oauth/authorize
          redirects here for non-trusted clients. Session-gated via the
          component itself so the user is bounced back through /login if
          they somehow land here without a live session. */}
      <Route path="/oauth/consent" element={<OAuthConsent />} />
      {/* Iter 108m — OAuth Tracer surfaces as a proper Conduit-UI app
          (top nav + sidebar via <Layout>). Gated by `system_logs.view`
          since it reads the same underlying `system_logs` table.
          Super-admins inherit this perm automatically. */}
      <Route path="/oauth-debug" element={<Protected perm="system_logs.view"><OAuthLiveDebug /></Protected>} />
      <Route path="/tlms-debug" element={<Protected><TlmsDebugTerminal /></Protected>} />
      <Route path="/quote-accept/:token" element={<QuoteAccept />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/users" element={<Navigate to="/settings" replace />} />
      <Route path="/partners" element={<Protected perm="partners.view"><Partners /></Protected>} />
      <Route path="/customers" element={<Protected perm="customers.view"><Customers /></Protected>} />
      <Route path="/organizations" element={<Navigate to="/customers" replace />} />
      <Route path="/permissions" element={<Protected perm="permissions.view"><Permissions /></Protected>} />
      <Route path="/apps" element={<Protected perm="apps.view"><Apps /></Protected>} />
      {/* Iter 130 — the app's own permission module authorises its page, so no
          blanket apps.view gate here: the backend decides and AppViewer shows
          the 403 screen when access is denied. */}
      <Route path="/apps/:appId" element={<Protected><AppViewer /></Protected>} />
      <Route path="/activity" element={<Protected perm="activity.view" anyPerm={["activity_logs.view", "system_logs.view", "audit_log.view", "user_roles.view"]}><ActivityLogs /></Protected>} />
      <Route path="/analytics" element={<Protected perm="analytics.view" anyPerm={["activity_logs.view"]}><Analytics /></Protected>} />
      <Route path="/requests" element={<Protected perm="apps.view"><Requests /></Protected>} />
      <Route path="/developer" element={<Protected perm="dashboard.view"><Developer /></Protected>} />
      <Route path="/quotes" element={<Protected perm="quote_builder.view"><Quotes /></Protected>} />
      <Route path="/inventory" element={<Navigate to="/inventory-hub" replace />} />
      <Route path="/hardware" element={<Navigate to="/inventory-hub/hardware" replace />} />
      <Route path="/inventory-hub" element={<Protected perm="inventory.view"><InventoryHub /></Protected>}>
        <Route index element={<Inventory />} />
        <Route path="hardware" element={<Hardware />} />
        <Route path="sip-trunks" element={<SipTrunks />} />
        <Route path="manufacturers" element={<Manufacturers />} />
        <Route path="did-master" element={<DidMaster />} />
        <Route path="network-keys" element={<NetworkAssessmentKeys />} />
      </Route>
      <Route path="/number-orders" element={<Protected perm="order_forms.view"><NumberOrders /></Protected>} />
      <Route path="/fax" element={<Protected perm="order_forms.view"><Fax /></Protected>} />
      <Route path="/commissions" element={<Protected perm="commissions.view"><Commissions /></Protected>} />
      <Route path="/commissions/payments" element={<Protected perm="commissions.view"><PaymentsTimeline /></Protected>} />
      <Route path="/commissions/accounting" element={<Protected><AccountingQueue /></Protected>} />
      <Route path="/commissions/engine" element={<Protected perm="commissions.view" staffOnly><CommissionsEngine /></Protected>} />
      <Route path="/commissions/partners/:key" element={<Protected perm="commissions.edit"><PartnerCommissions /></Protected>} />
      <Route path="/in-out" element={<Protected perm="dashboard.view"><InOut /></Protected>} />
      <Route path="/teams" element={<Protected perm="dashboard.view"><Teams /></Protected>} />
      <Route path="/teams/:name" element={<Protected perm="dashboard.view"><TeamPage /></Protected>} />
      {/* Iter 119b1 — KB (Knowledge Base v2), internal beta. Access is
          enforced by the backend against the `kb` app allow-list, so the
          route only needs a logged-in user; a denied user gets the
          AppUnavailable screen from the 403. */}
      <Route path="/kb" element={<Protected perm="dashboard.view"><KnowledgeBase /></Protected>} />
      <Route path="/kb/c/:slug" element={<Protected perm="dashboard.view"><KBCategory /></Protected>} />
      <Route path="/kb/c/:slug/print" element={<Protected perm="dashboard.view"><KBCategoryPrint /></Protected>} />
      <Route path="/kb/a/:slug" element={<Protected perm="dashboard.view"><KBArticle /></Protected>} />
      <Route path="/kb/a/:slug/edit" element={<Protected perm="dashboard.view"><KBArticleEditor /></Protected>} />
      <Route path="/kb/new" element={<Protected perm="dashboard.view"><KBArticleEditor /></Protected>} />
      <Route path="/kb/manage/articles" element={<Protected perm="dashboard.view"><KBManageArticles /></Protected>} />
      <Route path="/kb/studio" element={<Protected perm="dashboard.view"><KBContentStudio /></Protected>} />
      <Route path="/kb/import-review" element={<Navigate to="/settings?tab=kb-import-review" replace />} />
      <Route path="/kb/missing-assets" element={<Protected perm="dashboard.view"><KBMissingAssets /></Protected>} />
      <Route path="/kb/settings" element={<Navigate to="/settings?tab=kb-branding" replace />} />
      <Route path="/kb/manage" element={<Protected perm="dashboard.view"><KBManageCategories /></Protected>} />
      <Route path="/kb/snippets" element={<Protected perm="dashboard.view"><KBSnippets /></Protected>} />
      <Route path="/kb/callout-audit" element={<Protected perm="dashboard.view"><KBCalloutAudit /></Protected>} />
      <Route path="/kb/risk-review" element={<Protected perm="dashboard.view"><KBRiskReview /></Protected>} />
      <Route path="/kb/image-health" element={<Protected perm="kb_admin.view"><KBImageHealth /></Protected>} />
      <Route path="/kb/cleaned" element={<Protected perm="dashboard.view"><KBCleaned /></Protected>} />
      <Route path="/kb/danger-review" element={<Protected perm="dashboard.view"><KBDangerReview /></Protected>} />
      <Route path="/kb/planner" element={<Protected perm="dashboard.view"><KBPlanner /></Protected>} />
      <Route path="/kb/duplicates" element={<Protected perm="dashboard.view"><KBDuplicates /></Protected>} />
      <Route path="/kb/visibility" element={<Protected perm="dashboard.view"><KBVisibility /></Protected>} />
      <Route path="/kb/bulk-visibility" element={<Protected perm="dashboard.view"><KBBulkVisibility /></Protected>} />
      <Route path="/kb/category-health" element={<Protected perm="dashboard.view"><KBCategoryHealth /></Protected>} />
      <Route path="/kb/search-insights" element={<Protected perm="dashboard.view"><KBSearchInsights /></Protected>} />
      <Route path="/kb/suggestions" element={<Protected perm="dashboard.view"><KBSuggestions /></Protected>} />
      <Route path="/kb/usage" element={<Protected perm="dashboard.view"><KBUsage /></Protected>} />
      <Route path="/kb/feedback" element={<Protected perm="dashboard.view"><KBFeedbackDashboard /></Protected>} />
      <Route path="/kb/glossary" element={<Protected perm="dashboard.view"><KBGlossary /></Protected>} />
      <Route path="/kb/helpjuice" element={<Navigate to="/settings?tab=kb-helpjuice" replace />} />
      <Route path="/projects" element={<Protected perm="projects.view"><Projects /></Protected>} />
      <Route path="/qbr" element={<Protected perm="qbr.view"><QBR /></Protected>} />
      <Route path="/did-master" element={<Protected perm="did.view"><DidMaster /></Protected>} />
      <Route path="/key-master" element={<Protected perm="api_keys.view"><ApiKeyMaster /></Protected>} />
      <Route path="/sip-trace-analysis" element={<Protected perm="sip_trace.view"><SipTraceAnalysis /></Protected>} />
      <Route path="/service-desk" element={<Protected perm="service_desk.view"><ServiceDesk /></Protected>} />
      <Route path="/ask-claude" element={<Protected perm="apps.view"><AskClaude /></Protected>} />
      <Route path="/helpdesk" element={<Protected perm="helpdesk.view" superOnly><Helpdesk /></Protected>} />
      <Route path="/helpdesk/:ticketId" element={<Protected perm="helpdesk.view" superOnly><Helpdesk /></Protected>} />
      <Route path="/tasks" element={<Protected perm="dashboard.view"><Tasks /></Protected>} />
      <Route path="/ai-outbound" element={<Protected perm="apps.view"><AIOutbound /></Protected>} />
      <Route path="/ai-outbound/:campaignId" element={<Protected perm="apps.view"><AIOutbound /></Protected>} />
      <Route path="/jobs" element={<Protected perm="job_board.view"><JobBoard /></Protected>} />
      <Route path="/uc-hospitality" element={<Protected perm="apps.view"><UCHospitality /></Protected>} />
      <Route path="/changelog" element={<Protected perm="changelog.view"><Changelog /></Protected>} />
      <Route path="/price-list" element={<Protected><PriceList /></Protected>} />
      <Route path="/billing-sync/*" element={<Protected perm="billing_sync.view"><BillingSync /></Protected>} />
      {/* Iter 108t — Standalone Conduit-owned profile page for
          partner + customer users. Same editable fields as the staff
          profile (name, phone, birthday, avatar, password), but with
          no Humi/HR framing — Humi is UnitedCloud's internal HR
          portal and partners must never see vendor branding for it.
          Staff users continue to open /humi/profile via the account
          dropdown; the routing decision lives in Layout.jsx. */}
      <Route path="/profile" element={<Protected perm="profile.view"><Profile /></Protected>} />
      <Route path="/humi" element={<Protected perm="apps.view"><HumiLayout /></Protected>}>
        <Route index element={<HumiHome />} />
        <Route path="profile" element={<HumiProfile />} />
        <Route path="people" element={<HumiPeople />} />
        <Route path="time-off" element={<HumiTimeOff />} />
        <Route path="documents" element={<HumiDocuments />} />
        <Route path="training" element={<HumiTraining />} />
        <Route path="surveys" element={<HumiSurveys />} />
        <Route path="performance" element={<HumiPerformance />} />
        <Route path="reporting" element={<HumiReporting />} />
        <Route path="benefits" element={<HumiBenefits />} />
        <Route path="company" element={<HumiCompany />} />
        <Route path="addons" element={<HumiAddons />} />
      </Route>
      <Route path="/jobs/:postingId" element={<Protected perm="job_board.view"><JobBoard /></Protected>} />
      <Route path="/reports" element={<Protected perm="reports.view"><Reports /></Protected>} />
      <Route path="/reports/:reportKey" element={<Protected perm="reports.view"><Reports /></Protected>} />
      <Route path="/territory-aliases" element={<Protected perm="organizations.edit"><TerritoryAliases /></Protected>} />
      <Route path="/env-drift" element={<Protected perm="settings_platform.view"><EnvDrift /></Protected>} />
      <Route path="/data-sync" element={<Protected superOnly><DataSync /></Protected>} />
      <Route path="/productivity" element={<Protected><Productivity /></Protected>} />
      <Route path="/uc-cdr" element={<Protected perm="uc_cdr.view"><UCCDR /></Protected>} />
      <Route path="/emergency-numbers" element={<Protected perm="e911.view"><EmergencyNumbers /></Protected>} />
      <Route path="/clients" element={<Navigate to="/customers" replace />} />
      <Route path="/settings" element={<Protected perm="settings_*.view"><SettingsPage /></Protected>} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrandingProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </BrandingProvider>
    </div>
  );
}
